import {createSelector} from 'reselect';
import {DisplayFeature, DisplayFeatureType, GeometryType, SpecialAttribute, UserSession, ValueType} from './domain';
import FeaturesState, {GridCoordinates, QualityParam} from './domain/FeaturesState';
import MapUtils from '../../map/map-utils';
import {NvdbFeatureType} from '../../../integration/datacatalog/datacatalog-services';
import WKT from 'terraformer-wkt-parser';
import {allGeometriesCompatible, copyGeometryFilter} from './utils/copyGeometryFilter';
import {Comment} from '../../../integration/representation/contract';


const featureType = (state: FeaturesState) => state.selectedFeatureType as DisplayFeatureType;

export const selectedFeatureIds = (state: FeaturesState) => state.selectedFeatures as string[];
export const featureSearchString = (state: FeaturesState) => state.featureSearchString.toLowerCase() as string;
export const user = (state: FeaturesState) => state.user as UserSession;
export const contractId = (state: FeaturesState): string => state.contractId;
export const sortSetting = (state: FeaturesState) => state.sortSetting;
export const showSubmittedFeatures = (state: FeaturesState) => state.showSubmittedFeatures;
export const showOnlySelectedFeatures = (state: FeaturesState) => state.showOnlySelectedFeatures;
export const statePreserveFocus = (state: FeaturesState) => state.preserveFocus;
export const stateIsFetching = (state: FeaturesState) => state.isFetching;
export const stateShowQualityParams = (state: FeaturesState) => state.showQualityParams;
export const stateUseDescriptiveName = (state: FeaturesState) => state.useDescriptiveName;
const stateNvdbDatacatalog = (state: FeaturesState) => state.datacatalog;
export const stateDatacatalogFeatureTypes = (state: FeaturesState): NvdbFeatureType[] => state.datacatalog.getFeatureTypes();
const stateSelectedNvdbRoadObject = (state: FeaturesState) => state.selectedNvdbRoadObject;
const stateShowOnlyObjektlistAttributes = (state: FeaturesState) => state.showOnlyObjektlistAttributes;
const stateGridFocus = (state: FeaturesState) => state.gridFocus;
const stateShowEditor = (state: FeaturesState) => state.showEditor;
const stateFeatureTypes = (state: FeaturesState) => state.featureTypes;
export const missingAttributeTypes = (state: FeaturesState) => state.missingAttributeTypes;
export const fetchingMissingAttributeTypes = (state: FeaturesState) => state.fetchingMissingAttributeTypes;
export const isCopyGeometryModalVisible = (state: FeaturesState) => state.copyGeometryModalVisible;
export const sensitiveDataOnUrl = (state: FeaturesState) => state.sensitiveAttributesInURL;
export const fetchingSensitiveDataOnUrl = (state: FeaturesState) => state.fetchingSensitiveAttributeTypes;
export const copiableGeometries = (state: FeaturesState) => state.copiableGeometries;
export const isFetchingCopiableFeatureGeometries = (state: FeaturesState) => state.fetchingCopiableGeometries;
export const isCopyingFeatureGeometries = (state: FeaturesState) => state.copyingGeometriesBetweenFeatures;
export const roadSystemReferences = (state: FeaturesState) => state.roadSystemReferences;
export const isDeleteManyCommentsModalVisible = (state: FeaturesState) => state.commentsModalVisible;
export const commentsFilter = (state: FeaturesState) => state.commentsFilter;
export const markedComments = (state: FeaturesState) => state.markedComments;
export const featureOrTypeIdForComments = (state: FeaturesState) => state.featureOrTypeIdForComments;
export const isCreateObjectsVisible = (state: FeaturesState) => state.createObjectModalVisible;
export const createObjectTypeId = (state: FeaturesState) => state.createObjectTypeId;
export const createdObjectWkt = (state: FeaturesState) => state.createObjectWkt;
export const creatingObjectGeometry = (state: FeaturesState) => state.creatingObjectGeometry;
const stateRoadSegments = (state: FeaturesState) => state.roadSegments;
const stateSelectedRoadSegment = (state: FeaturesState) => state.selectedRoadSegment;
export const availableComments = (state: FeaturesState) => state.comments;
export const availableFeatures = (state: FeaturesState) => state.features;
export const loadingAttributes = (state: FeaturesState) => state.loadingAttributes;
export const loadingSpecialAttributes = (state: FeaturesState) => state.loadingSpecialAttributes;
export const stateReferenceError = (state: FeaturesState) => state.referenceError;

export const featureTypes = createSelector(
    [stateFeatureTypes], (featureTypes) => {
        return featureTypes;
    }
);

export const selectedNvdbRoadObject = createSelector(
    [stateSelectedNvdbRoadObject], (roadObject) => {
        return roadObject;
    }
);

const datacatalog = createSelector(
    [stateNvdbDatacatalog], (datacatalog) => {
        return datacatalog;
    }
);

export const availableRoadObjectTypesForNvdbImport = createSelector(
    [selectedNvdbRoadObject, featureTypes, datacatalog], (roadObject, localTypes, datacatalog) => {

        const dcTypes = datacatalog?.getFeatureTypes() ?? [];
        const wkt = roadObject?.geometry?.wkt;
        if (!wkt) return [];
        const geometryType = MapUtils.getGeometryTypeFromWkt(wkt);

        const lockedTypes = localTypes.filter(type => type.isLocked);

        const validTypes = dcTypes
            .filter(type => !lockedTypes.some(locked => locked.typeid == type.id))          // ingen låste typer
            .filter(type => type.geometryTypes.some(geoType => geoType == geometryType))    // kun kompatible typer
            .map(type => {
                return {id: type.id, name: type.name} as RoadObjectTypeOption;
            });
        return validTypes;
    }
);

export type RoadObjectTypeOption = {
    id: number,
    name: string
};


export const specialAttributes = createSelector(
    [], () => {
        const specialAttributes: SpecialAttribute[] = [
            'OPERASJON',
            'VEGSYSTEMREFERANSE',
            'SIDEPOSISJON',
            'FELT'
        ];
        return specialAttributes;
    }
);

// Antall kolonner som ikke kommer fra iterering over vegobjektets egenskaper.
export const numberOfNonAttributeColumns = createSelector(
    [specialAttributes], (specialAttributes) => {
        return specialAttributes.length;
    }
);

export const showOnlyObjektlistAttributes = createSelector(
    [stateShowOnlyObjektlistAttributes],
    (showOnlyObjektlistAttributes) => showOnlyObjektlistAttributes
);

const showQualityParams = createSelector(
    [stateShowQualityParams],
    (showQualityParams) => showQualityParams
);

export const gridFocus = createSelector(
    [stateGridFocus],
    (gridFocus) => gridFocus
);

export const showEditor = createSelector(
    [stateShowEditor],
    (showEditor) => showEditor
);

export const baseFeatures = createSelector(
    [availableFeatures, selectedFeatureIds], (features, selectedFeatureIds) => {
        return features
            .map(feature => ({
                ...feature,
                isSelected: (selectedFeatureIds ?? []).includes(feature.id)
            }) as DisplayFeature);
    }
);

const attributeTypeIdsFromFeatures = createSelector(
    [baseFeatures],
    (baseFeatures) => {
        return baseFeatures
            .map(feature => {
                return feature.attributes
                    .map(attribute => {
                        return attribute.typeId;
                    });
            })
            .reduce((acc, id) => { // Flatten array of arrays
                return acc.concat(id);
            }, [])
            .filter((elm, index, array) => array.indexOf(elm) === index); // This is Array.uniq()
    }
);


export const qualityParams = createSelector(
    [showQualityParams],
    (showQualityParams) => {
        let qualityParams: QualityParam[] = [];
        if (showQualityParams) {
            qualityParams = qualityParams.concat([
                new QualityParam('measurementMethod',  'Målemetode', ValueType.STRING),
                new QualityParam('precision',  'Nøyaktighet', ValueType.NUMBER),
                new QualityParam('measurementMethodHeight',  'Målemetode høyde', ValueType.STRING),
                new QualityParam('precisionHeight',  'Nøyaktighet høyde', ValueType.NUMBER),
                new QualityParam('datafangstdato', 'Datafangstdato', ValueType.STRING),
                new QualityParam('heightReference', 'Høydereferanse', ValueType.STRING),
            ]);
        }
        return qualityParams;
    }
);

export const attributeTypes = createSelector(
    [showOnlyObjektlistAttributes, showQualityParams, featureType, attributeTypeIdsFromFeatures],
    (showOnlyObjektlistAttributes, showQualityParams, featureType, attributeTypeIdsFromFeatures) => {

        if (!featureType.attributeTypes) {
            return [];
        }

        return featureType.attributeTypes
            .filter(attributeType => {
                return (
                    !showOnlyObjektlistAttributes ||
                    attributeType.inObjectList ||
                    (showOnlyObjektlistAttributes && attributeTypeIdsFromFeatures.includes(attributeType.id)));
            });
    }
);

export const filteredFeatures = createSelector(
    [baseFeatures, showSubmittedFeatures, showOnlySelectedFeatures],
    (features, showSubmittedFeatures, showOnlySelectedFeatures) => {

        return features
            .filter(feature => {
                return showSubmittedFeatures || !feature.isExporting;
            })
            .filter(feature => {
                return !(showOnlySelectedFeatures && !feature.isSelected);
            });
    }
);

export const selectedFeatures = createSelector(
    [baseFeatures, selectedFeatureIds],
    (features, ids) => {
        return (ids ?? []).map(id => features.find(feature => feature.id === id))
            .filter(feature => feature !== undefined);
    }
);

export const selectedEditableFeatures = createSelector(
    [selectedFeatures], (features) => {
        return features.filter(feature => !feature.isLocked && feature.operation !== 'CLOSE' && !feature.isExporting);
    }
);

export const allEditableFeatures = createSelector(
    [baseFeatures], (features) => {
        return features.filter(feature => !feature.isLocked && feature.operation !== 'CLOSE' && !feature.isExporting);
    }
);


export const selectedGeometryTypes = createSelector(
    [selectedFeatures],
    (features: DisplayFeature[]): GeometryType[] => {
        const uniqueGeometryTypes =  new Set(features.map(f => MapUtils.getGeometryTypeFromWkt(f.geometry.wkt)));
        return Array.from(uniqueGeometryTypes);
    }
);

export const supportedGeometriesForSelectedFeatures = createSelector(
    [selectedGeometryTypes, stateFeatureTypes, stateDatacatalogFeatureTypes],
    (selectedGeometryTypes, contractFeatureTypes, allFeatureTypes): NvdbFeatureType[] => {
        const lockedTypes = contractFeatureTypes.filter(ft => ft.isLocked).map(ft => ft.typeid);
        return allFeatureTypes.filter(ft => {
            // Can't use type locked in contract
            if (lockedTypes.some(typeId => typeId === ft.id)) {
                return false;
            }

            // Feature type must support all feature types for selected objects
            return selectedGeometryTypes.every(type => ft.geometryTypes.includes(type));
        });
    }
);

export const locationalLaneIsAllowed = createSelector(
    [featureType],
    (selectedFeatureType) => {
        return selectedFeatureType?.laneRelevance !== 'CANNOT';
    }
);

export const selectedFeatureType = createSelector(
    [featureType],
    (selectedFeatureType) => {
        return selectedFeatureType;
    }
);

// Endpoint Selectors //

export const features = createSelector(
    [filteredFeatures], (filteredFeatures) => {
        return filteredFeatures;
    }
);

export const gridSize = createSelector(
    [features, attributeTypes, numberOfNonAttributeColumns, qualityParams],
    (features, attributeTypes, nonAttributes, qualityParams) => {
        return {x: attributeTypes.length + nonAttributes + qualityParams.length, y: features.length};
    }
);

export const userCanResolveIssues = createSelector(
    [user, contractId], (user, contractId) => {
        return user?.hasPermission('FEATURE:RESOLVE_ISSUE', contractId) ?? false;
    }
);

export const userCanToggleFeatureLock = createSelector(
    [user, contractId], (user, contractId) => {
        return user?.hasPermission('FEATURE_LOCK:TOGGLE', contractId) ?? false;
    }
);

export const userCanEditFeature = createSelector(
    [user, contractId],
    (user, contractId) => {
        return user?.hasPermission('FEATURE:UPDATE', contractId) ?? false;
    }
);

export const userCanCreateFeature = createSelector(
    [user, contractId],
    (user, contractId) => {
        return user?.hasPermission('FEATURE:CREATE', contractId) ?? false;
    }
);

export const userCanDeleteManyComments = createSelector(
    [user, contractId],
    (user, contractId) => {
        return (user?.hasPermission('FEATURE_TYPE_COMMENT:DELETE', contractId) ?? false) &&
            (user?.hasPermission('FEATURE_COMMENT:DELETE', contractId) ?? false);
    }
);

export const userCanToggleFeatureTypeLock = createSelector(
    [user, contractId],
    (user, contractId) => {
        return user?.hasPermission('FEATURE_TYPE_LOCK:TOGGLE', contractId) ?? false;
    }
);

const preservedFocusFeatureIndex = createSelector(
    [features, statePreserveFocus],
    (features, preserveFocus) => {
        if (preserveFocus) {
            const index = features.findIndex(f => f.id === preserveFocus.featureId);
            if (index > -1) return index;
        }
        return null;
    }
);

export const needToNavigate = createSelector(
    [stateGridFocus, gridSize, preservedFocusFeatureIndex, statePreserveFocus, stateIsFetching],
    (gridFocus, gridSize, preservedFocusFeatureIndex, preserveFocus, isFetching) => {
        let focus = gridFocus;

        // check need to preserve Focus
        if (preserveFocus && preservedFocusFeatureIndex !== null) {
            if ((preserveFocus.waitUntilLoaded && !isFetching) || !preserveFocus.waitUntilLoaded) {
                focus = new GridCoordinates(focus.x, preservedFocusFeatureIndex);
            }
        }
        // Empty grid result? Reset focus to first index
        if (gridSize.y == 0) {
            focus = new GridCoordinates(0, 0);
        }
        // handle focused object out of bounds
        else if (focus.y > gridSize.y - 1 || focus.x > gridSize.x - 1) {
            focus = new GridCoordinates(
                Math.min(focus.x, gridSize.x - 1),
                Math.min(focus.y, gridSize.y - 1)
            );
        }
        if (!focus.isEqualTo(gridFocus)) {
            return focus;
        }
        // If we reach here, focus didn't change
        return null;
    }
);

export const focusedFeature = createSelector(
    [features, gridFocus],
    (features, focus) => {
        return features[focus.y];
    }
);

export const numberOfRoadObjectsToUpdateWhenCopyToAll = createSelector(
    [selectedFeatureType],
    (type) => {
        if (type) {
            return type.editableFeatures - 1;
        }
        return -1;
    }
);

export const focusedAttributeType = createSelector(
    [gridFocus, numberOfNonAttributeColumns, attributeTypes, qualityParams],
    (focus, offset, attributeTypes, qualityParams) => {
        return attributeTypes[focus.x - offset - qualityParams.length];
    }
);

export const focusedAttribute = createSelector(
    [focusedFeature, focusedAttributeType],
    (feature, attributeType) => {
        if (!attributeType || !feature) {
            return null;
        }
        return feature.attributes.find(attribute => attribute.typeId === attributeType.id);
    }
);

export const focusedSpecialAttribute = createSelector(
    [gridFocus, specialAttributes],
    (gridFocus, specialAttributes) => {
        return specialAttributes[gridFocus.x];
    }
);

export const sidePositionIsAllowed = createSelector(
    [featureType], (featureType) => {
        return featureType?.sidePositionRelevance !== 'CANNOT';
    }
);

export const getGeometryCopyDirection = createSelector([selectedFeatureIds], (selectedFeatures) => {
    if (selectedFeatures.length === 0) {
        return 'UNKNOWN';
    } else if (selectedFeatures.length === 1) {
        return 'FROM';
    } else {
        return 'TO';
    }
});

// Denne kan erstatte et endepunkt om vi har alle features lastet inn på en gang,
// men per nå er det delt på valgt feature type id
export const getCopiableGeometries = createSelector(
    [stateNvdbDatacatalog, features, selectedFeatures, getGeometryCopyDirection],
    (catalog, features, selectedFeatures, direction) => {
        if (selectedFeatures.length === 0) {
            return [];
        }

        if (direction === 'FROM') {
            // Get our only selected feature
            const onlySelectedGeometryType = WKT.parse(selectedFeatures[0].geometry.wkt).type;
            // Find which we can copy this one features geometry into
            return features.filter(feature => {
                if (feature.id === selectedFeatures[0].id) {
                    return false;
                }
                return copyGeometryFilter(onlySelectedGeometryType, catalog.getFeatureType(feature.typeId));
            });
        } else if (direction === 'TO') {
            // We have several elements we want to copy geometry into, and need to find all that fits all
            return features.filter(feature => allGeometriesCompatible(catalog.getFeatureType(feature.typeId), selectedFeatures.map(selectedFeature => WKT.parse(selectedFeature.geometry.wkt).type)));
        }
        return [];
});

export type FeatureComments = {
    featureName: string;
    comments: Comment[];
};

export type FeatureTypeComments = {
    featureTypeName: string;
    comments: Comment[];
    features: Record<string, FeatureComments>
};

export type OrderedComments = Record<number, FeatureTypeComments>;

export const featureOrTypeForComments = createSelector([featureOrTypeIdForComments, selectedFeatureType, features],
    (id, type, features) => {
        if (id) {
            if (typeof id === 'number') {
                return type.typeId === id ? type : null;
            } else {
                return features.find(feature => feature.id === id) ?? null;
            }
        }
        return null;
    });

export const displayFeatureTypeFromDisplayFeatureForComment = createSelector([selectedFeatureType, featureOrTypeForComments], (firstSelectedType, singleSelectedFeatureOrFeatureIdForComments) => {
    if (singleSelectedFeatureOrFeatureIdForComments !== null && 'id' in singleSelectedFeatureOrFeatureIdForComments && firstSelectedType && singleSelectedFeatureOrFeatureIdForComments.typeId === firstSelectedType.typeId) {
        return firstSelectedType;
    }
    return null;
});

export const getSelectedComments = createSelector(
    [availableComments, datacatalog, featureOrTypeForComments, selectedFeatureIds, selectedFeatures, selectedFeatureType],
    (availableComments, catalog, singleFeatureComments, selectedFeatureIds, selectedFeatures, selectedFeatureType) => {
        // A single feature or type is clicked (opened from icon)
        if(singleFeatureComments !== null){
            // We have no comments
            if(!availableComments[singleFeatureComments.typeId]) {
                return {};
            } else if ('id' in singleFeatureComments){ // A single feature is opened from icon
                return {
                    [singleFeatureComments.typeId]: {
                        ...availableComments[singleFeatureComments.typeId],
                        comments: [],
                        features: availableComments[singleFeatureComments.typeId]?.features[singleFeatureComments.id] ? {
                            [singleFeatureComments.id]: availableComments[singleFeatureComments.typeId].features[singleFeatureComments.id]
                        } : {}
                    }
                };
            } else { // A single feature type comments are opened from icons
                return {
                    [singleFeatureComments.typeId]: {
                        ...availableComments[singleFeatureComments.typeId],
                        features: {}
                    }
                };
            }
        } else {
            // Features are selected (could be multiple feature types)
            if(selectedFeatures.length > 0){
                const selectedFeatureTypeIds = selectedFeatures.map(feature => feature.typeId);
                const selectedComments: OrderedComments = {};
                Object.entries(availableComments).forEach(([id, entry]) => {
                    const parsedId = Number.parseInt(id, 10);
                    if(!Number.isNaN(parsedId) && selectedFeatureTypeIds.includes(parsedId)){
                        const includedFeatureComments: Record<string, FeatureComments> = Object.entries(entry.features)
                            .filter(([featureId, featureComments]) => selectedFeatureIds.includes(featureId) && featureComments.comments.length > 0)
                            .reduce((prev: Record<string, FeatureComments>, [currId, currFeatureComments]) => ({
                                ...prev,
                                [currId]: currFeatureComments
                            }), {});
                        if(Object.keys(includedFeatureComments).length > 0){
                            selectedComments[parsedId] = {
                                featureTypeName: availableComments[parsedId].featureTypeName,
                                comments: [],
                                features: includedFeatureComments
                            };
                        }
                    }
                });
                return selectedComments;
            } else {
                // Feature types may be selected, otherwise this will return an empty object
                return Object.entries(availableComments)
                    .filter(([id, value]) => selectedFeatureType.typeId === Number.parseInt(id, 10) && (value.comments.length > 0 || Object.values(value.features).some(feature => feature.comments.length > 0)))
                    .reduce((prev: OrderedComments, [currId, currTypeComments]) => ({...prev, [currId]: currTypeComments}), {});
            }
        }
    }
);

const filterComments = (comment: Comment, name: string, filter: string) => {
    const lowerCaseFilter = filter.toLocaleLowerCase();
    return filter.length === 0 ||
        name.toLocaleLowerCase().includes(lowerCaseFilter) ||
        comment.username.toLocaleLowerCase().includes(lowerCaseFilter) ||
        comment.comment.toLocaleLowerCase().includes(lowerCaseFilter);
};

export const getFilteredComments = createSelector<FeaturesState, OrderedComments, string, OrderedComments>([getSelectedComments, commentsFilter], (allComments: OrderedComments, filter) => {
    return Object.entries(allComments)
        .map(([id, typeComments]) => ({
                [Number.parseInt(id, 10)]: {
                    ...typeComments,
                    comments: typeComments.comments.filter((comment) => filterComments(comment, typeComments.featureTypeName, filter)),
                    features: Object.entries(typeComments.features)
                        .map(([featureId, featureComments]): Record<string, FeatureComments> => ({
                            [featureId]: {
                                ...featureComments,
                                comments: featureComments.comments.filter(comment => filterComments(comment, featureComments.featureName, filter))
                            }
                        }))
                        .reduce((prev, curr) => ({...prev, ...curr}), {})
                }
            })
        )
        .reduce((prev: OrderedComments, curr) => ({...prev, ...curr}), {});
});

export const roadSegments = createSelector(
    [stateRoadSegments], (roadSegments) => {
        return roadSegments;
    }
);

export const selectedRoadSegment = createSelector(
    [stateSelectedRoadSegment], (selectedRoadSegment) => {
        return selectedRoadSegment;
    }
);
